import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingScreenProps {
  message?: string;
  fullScreen?: boolean;
}

/**
 * LoadingScreen Component
 * 
 * Industry-standard loading indicator for code-split routes
 * 
 * Features:
 * - Centered spinner with animation
 * - Optional loading message
 * - Accessible (ARIA live region)
 * - Dark mode support
 * 
 * Used By:
 * - React.lazy() Suspense fallback
 * - Route transitions
 * - Data loading states
 * 
 * Pattern: GitHub, Linear, Stripe lazy loading
 */
export const LoadingScreen: React.FC<LoadingScreenProps> = ({
  message = 'Loading...',
  fullScreen = true
}) => {
  const containerClass = fullScreen
    ? 'min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900'
    : 'flex items-center justify-center p-8';

  return (
    <div className={containerClass}>
      <div className="text-center">
        {/* Spinner */}
        <Loader2 
          className="w-12 h-12 mx-auto text-blue-600 dark:text-blue-400 animate-spin" 
          aria-hidden="true"
        />
        
        {/* Loading Message - Accessible */}
        <p 
          className="mt-4 text-sm font-medium text-gray-600 dark:text-gray-400"
          role="status"
          aria-live="polite"
        >
          {message}
        </p>
      </div>
    </div>
  );
};

export default LoadingScreen;

